/**
 * Token refresh queue system to prevent race conditions
 * Ensures only one refresh operation runs at a time
 */

interface RefreshRequest {
  id: string;
  resolve: (tokens: RefreshTokens | null) => void;
  reject: (error: Error) => void;
  timestamp: number;
}

interface RefreshTokens {
  accessToken: string;
  refreshToken?: string;
  expiresIn: number;
}

class AuthRefreshQueue {
  private queue: RefreshRequest[] = [];
  private isRefreshing = false;
  private lastRefreshTime = 0;
  private readonly MIN_REFRESH_INTERVAL = 1000; // 1 second minimum between refreshes
  private readonly MAX_QUEUE_SIZE = 10;
  private readonly QUEUE_TIMEOUT = 30000; // 30 seconds

  /**
   * Add a refresh request to the queue
   */
  async refreshToken(): Promise<RefreshTokens | null> {
    const id = this.generateRequestId();

    return new Promise((resolve, reject) => {
      // Check queue size limit
      if (this.queue.length >= this.MAX_QUEUE_SIZE) {
        reject(new Error('Refresh queue is full'));
        return;
      }

      // Check if we recently refreshed
      const now = Date.now();
      if (now - this.lastRefreshTime < this.MIN_REFRESH_INTERVAL) {
        // If we just refreshed, return success immediately
        // The caller should use the already refreshed tokens
        resolve(null);
        return;
      }

      const request: RefreshRequest = {
        id,
        resolve,
        reject,
        timestamp: now
      };

      this.queue.push(request);

      // Set timeout for this specific request
      setTimeout(() => {
        this.removeFromQueue(id);
        reject(new Error('Refresh request timed out'));
      }, this.QUEUE_TIMEOUT);

      this.processQueue();
    });
  }

  /**
   * Process the refresh queue
   */
  private async processQueue(): Promise<void> {
    if (this.isRefreshing || this.queue.length === 0) {
      return;
    }

    this.isRefreshing = true;

    try {
      // Get all pending requests
      const requests = [...this.queue];
      this.queue = [];

      // Perform the actual refresh
      const tokens = await this.performRefresh();

      // Resolve all requests with the result
      this.lastRefreshTime = Date.now();
      requests.forEach(request => {
        request.resolve(tokens);
      });

    } catch (error) {
      // Reject all requests with the error
      const errorObj = error instanceof Error ? error : new Error('Refresh failed');
      this.queue.forEach(request => {
        request.reject(errorObj);
      });
      this.queue = [];
    } finally {
      this.isRefreshing = false;

      // Check if new requests were added while we were refreshing
      if (this.queue.length > 0) {
        // Use setTimeout to prevent infinite recursion
        setTimeout(() => this.processQueue(), 0);
      }
    }
  }

  /**
   * Perform the actual token refresh
   * This method should be overridden with the actual refresh logic
   */
  private async performRefresh(): Promise<RefreshTokens | null> {
    // Import here to avoid circular dependencies
    const { refreshApi } = await import('@/services/api/services/auth');

    try {
      const result = await refreshApi();
      return result;
    } catch (error) {
      console.error('[AuthRefreshQueue] Refresh failed:', error);
      throw error;
    }
  }

  /**
   * Remove a request from the queue by ID
   */
  private removeFromQueue(id: string): void {
    this.queue = this.queue.filter(request => request.id !== id);
  }

  /**
   * Generate a unique request ID
   */
  private generateRequestId(): string {
    return `refresh_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Get queue status (for debugging)
   */
  getStatus(): {
    isRefreshing: boolean;
    queueLength: number;
    lastRefreshTime: number;
  } {
    return {
      isRefreshing: this.isRefreshing,
      queueLength: this.queue.length,
      lastRefreshTime: this.lastRefreshTime
    };
  }

  /**
   * Clear the queue (used during logout)
   */
  clearQueue(): void {
    // Reject all pending requests
    this.queue.forEach(request => {
      request.reject(new Error('Refresh queue cleared'));
    });
    this.queue = [];
    this.isRefreshing = false;
  }

  /**
   * Check if a refresh is currently in progress
   */
  get isCurrentlyRefreshing(): boolean {
    return this.isRefreshing;
  }
}

// Export singleton instance
export const authRefreshQueue = new AuthRefreshQueue();